import { ActionModal, FormLayout } from '@kcftech/design-system';
import { AssemblyApplicationTypeCreateRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Filter } from 'utils/query_v3';
import { appendToRoute } from 'utils/RouteHelper';

export type CreateAssemblyTypeProps = Readonly<{
    onClose: (saved: boolean) => void;
}>;

type CreateAssemblyTypeForm = Readonly<{
    name: string;
    code: string;
}>;

const validateName = async (value: string) => {
    const assemblyTypeNameFiltered = await sdClient.getAssemblyApplicationTypes({
        filter: Filter.eq('Name', value),
    });

    if (assemblyTypeNameFiltered.Result.length >= 1) {
        return 'Name already exists. Please enter a unique name.';
    }

    return undefined;
};

const validateCode = async (value: string) => {
    const assemblyTypeNameFiltered = await sdClient.getAssemblyApplicationTypes({
        filter: Filter.eq('Code', value),
    });

    if (assemblyTypeNameFiltered.Result.length >= 1) {
        return 'Name already exists. Please enter a unique name.';
    }

    return undefined;
};

const maxAssemblyTypeNameLength = 50;
const nameRules: RegisterOptions = {
    required: 'Please enter the assembly type name',
    maxLength: {
        value: maxAssemblyTypeNameLength,
        message: `Assembly type name cannot be greater than ${maxAssemblyTypeNameLength} characters`,
    },
    validate: validateName,
};

const maxAssemblyTypeCodeLength = 16; // 32?
const codeRules: RegisterOptions = {
    required: 'Please enter the assembly type code',
    maxLength: {
        value: maxAssemblyTypeCodeLength,
        message: `Assembly type code cannot be greater than ${maxAssemblyTypeCodeLength} characters`,
    },
    validate: validateCode,
};

export function CreateAssemblyTypeModal({ onClose }: CreateAssemblyTypeProps) {
    const history = useHistory();
    const { showToast } = useNotificationContext();
    const { url } = useRouteMatch();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateAssemblyTypeForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    const { mutate: createAssemblyApplicationType, isPending } = useMutation({
        meta: { name: 'create-assembly-application-type' },
        mutationFn: async (request: AssemblyApplicationTypeCreateRequest) =>
            sdClient.createAssemblyApplicationType({ body: request }),

        onSuccess: (response) => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully created assembly type',
            });
            history.push(appendToRoute(url, response.Id));
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to create assembly type' }),
    });

    const onSave = handleSubmit((formData) =>
        createAssemblyApplicationType(
            new AssemblyApplicationTypeCreateRequest({
                Name: formData.name.trim(),
                Code: formData.code.trim(),
                Active: true,
            }),
        ),
    );

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(false)}
            title="Create Assembly Type"
            height={350}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <FormLayout>
                <ValidatableLabel
                    ariaLabel="create assembly type name label"
                    label="Name"
                    error={errors.name}
                    required
                    className="mb-20"
                >
                    <TextInput
                        autoFocus
                        placeholder="Enter a name for the assembly type"
                        invalid={errors.name !== undefined}
                        {...register('name', { ...nameRules })}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    ariaLabel="create assembly type code label"
                    label="Code"
                    error={errors.code}
                    required
                    className="mb-20"
                >
                    <TextInput
                        placeholder="Enter a code for the assembly type"
                        invalid={errors.code !== undefined}
                        {...register('code', { ...codeRules })}
                    />
                </ValidatableLabel>
            </FormLayout>
        </ActionModal>
    );
}
